import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { Save, AlertTriangle, Palette } from 'lucide-react';
import { PageTooltip } from '../components/TooltipSystem';
import { BrandColorPicker } from '../components/BrandColorPicker';

const TIER_KEYS = ['bronze', 'silver', 'gold', 'platinum'];
const DEFAULT_TIER_COLORS = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700', platinum: '#888888' };
const DEFAULT_TIER_EMOJIS = { bronze: '', silver: '', gold: '', platinum: '' };

export default function AdminProgram() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expiring, setExpiring] = useState(false);

  useEffect(() => {
    adminApi.get('/business/settings')
      .then(r => { setSettings(r.data.settings); setForm(r.data.settings); })
      .catch(() => toast.error('Errore caricamento impostazioni'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        punti_per_euro: parseFloat(form.punti_per_euro) || 1,
        welcome_points: parseInt(form.welcome_points) || 50,
        referral_points_giver: parseInt(form.referral_points_giver) || 100,
        referral_points_receiver: parseInt(form.referral_points_receiver) || 50,
        tier_thresholds: {
          silver: parseInt(form.tier_thresholds?.silver) || 200,
          gold: parseInt(form.tier_thresholds?.gold) || 500,
          platinum: parseInt(form.tier_thresholds?.platinum) || 1000,
        },
        tier_names: {
          bronze: (form.tier_names?.bronze || 'Bronze').trim(),
          silver: (form.tier_names?.silver || 'Silver').trim(),
          gold: (form.tier_names?.gold || 'Gold').trim(),
          platinum: (form.tier_names?.platinum || 'Platinum').trim(),
        },
        tier_multipliers: {
          bronze: parseFloat(form.tier_multipliers?.bronze) || 1,
          silver: parseFloat(form.tier_multipliers?.silver) || 1.25,
          gold: parseFloat(form.tier_multipliers?.gold) || 1.5,
          platinum: parseFloat(form.tier_multipliers?.platinum) || 2,
        },
        tier_colors: {
          bronze: form.tier_colors?.bronze || '#CD7F32',
          silver: form.tier_colors?.silver || '#C0C0C0',
          gold: form.tier_colors?.gold || '#FFD700',
          platinum: form.tier_colors?.platinum || '#888888',
        },
        tier_emojis: {
          bronze: (form.tier_emojis?.bronze || '').trim(),
          silver: (form.tier_emojis?.silver || '').trim(),
          gold: (form.tier_emojis?.gold || '').trim(),
          platinum: (form.tier_emojis?.platinum || '').trim(),
        },
        points_expiry_enabled: !!form.points_expiry_enabled,
        points_expiry_months: parseInt(form.points_expiry_months) || 12,
        points_expiry_mode: form.points_expiry_mode || 'relative',
        points_expiry_fixed_date: form.points_expiry_fixed_date ?? null,
        notification_delay_minutes: Math.max(0, parseInt(form.notification_delay_minutes ?? 0) || 0),
      };
      await adminApi.put('/business/settings', payload);
      // Re-sync `settings` so isDirty resets to false on success.
      setSettings(s => ({ ...(s || {}), ...payload }));
      toast.success('Impostazioni salvate!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il salvataggio');
    } finally { setSaving(false); }
  };

  // Track unsaved-changes — JSON-shallow compare. Surfaces a dot next to the
  // Save button + a sticky banner so the user can't navigate away thinking
  // their toggles auto-saved.
  const isDirty = React.useMemo(() => {
    if (!settings) return false;
    try { return JSON.stringify(settings) !== JSON.stringify(form); }
    catch { return false; }
  }, [settings, form]);

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const Field = ({ label, desc, value, onChange, type = 'number', min = 0 }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0">
      <div>
        <p className="text-[#1A1A1A] text-sm font-medium">{label}</p>
        {desc && <p className="text-[#64748B] text-xs mt-0.5">{desc}</p>}
      </div>
      <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        min={min}
        className="w-24 h-10 bg-slate-50 border border-slate-200 text-[#1A1A1A] rounded-xl px-3 text-sm text-center font-semibold focus:border-[#D44E20] focus:outline-none"
      />
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Programma</h1>
          <p className="text-[#64748B] text-sm">Configura le regole del programma fedeltà</p>
        </div>
        <button onClick={handleSave} disabled={saving || !isDirty} data-testid="save-settings-btn" className="relative flex items-center gap-2 px-5 py-2.5 bg-[#D44E20] text-white rounded-xl text-sm font-semibold hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
          <Save size={16} /> {saving ? 'Salvataggio...' : 'Salva'}
          {isDirty && !saving && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full ring-2 ring-white" data-testid="unsaved-dot" />
          )}
        </button>
      </div>

      {isDirty && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center gap-2 text-amber-800 text-xs" data-testid="unsaved-banner">
          <AlertTriangle size={14} />
          Hai modifiche non salvate. Clicca "Salva" prima di lasciare la pagina.
        </div>
      )}

      <div className="space-y-6">
        {/* Regole punti */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-1">Regole punti</h3>
          <p className="text-[#64748B] text-xs mb-3">Come vengono assegnati i punti</p>
          <Field label="Punti per euro speso" desc="Moltiplicato dal tier del cliente" value={form.punti_per_euro} onChange={v => setForm(f => ({ ...f, punti_per_euro: v }))} min={0.1} />
          <Field label="Punti benvenuto" desc="Assegnati alla registrazione" value={form.welcome_points} onChange={v => setForm(f => ({ ...f, welcome_points: v }))} />
          <Field label="Punti referral (chi porta)" value={form.referral_points_giver} onChange={v => setForm(f => ({ ...f, referral_points_giver: v }))} />
          <Field label="Punti referral (chi viene portato)" value={form.referral_points_receiver} onChange={v => setForm(f => ({ ...f, referral_points_receiver: v }))} />
        </div>

        {/* Notifica punti ritardata + finestra annullo */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-1">Notifica punti & annullo</h3>
          <p className="text-[#64748B] text-xs mb-3">
            Ritardo prima di inviare la notifica WhatsApp/Push al cliente dopo un acquisto.
            Lo staff può annullare la transazione entro la stessa finestra.
            Imposta <span className="font-semibold">0</span> per inviare subito (e disabilitare l'annullo oltre 5 minuti).
          </p>
          <Field
            label="Ritardo notifica (minuti)"
            desc="Es. 5 = lo staff ha 5 min per annullare un errore prima che la notifica parta"
            value={form.notification_delay_minutes}
            onChange={v => setForm(f => ({ ...f, notification_delay_minutes: v }))}
            min={0}
          />
        </div>

        {/* Tier configuration */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-1 flex items-center gap-2"><Palette size={16} /> Configurazione Tier</h3>
          <p className="text-[#64748B] text-xs mb-4">Personalizza nomi, colori, emoji, soglie e moltiplicatori di ogni livello</p>
          <div className="space-y-4">
            {TIER_KEYS.map(key => {
              const tierColor = form.tier_colors?.[key] || DEFAULT_TIER_COLORS[key];
              const tierName = form.tier_names?.[key] || key.charAt(0).toUpperCase() + key.slice(1);
              return (
                <div key={key} className="rounded-xl border border-slate-100 p-4" style={{ borderLeftWidth: 4, borderLeftColor: tierColor }}>
                  <PageTooltip page="program" />
                  <div className="grid grid-cols-12 gap-3 items-center">
                    {/* Color picker */}
                    <div className="col-span-1">
                      <BrandColorPicker
                        value={tierColor}
                        onChange={v => setForm(f => ({ ...f, tier_colors: { ...f.tier_colors, [key]: v } }))}
                        size={32}
                        showHexInput={false}
                        testid={`tier-color-${key}`}
                      />
                    </div>
                    {/* Emoji */}
                    <div className="col-span-1">
                      <input type="text" maxLength={2}
                        value={form.tier_emojis?.[key] ?? DEFAULT_TIER_EMOJIS[key]}
                        onChange={e => setForm(f => ({ ...f, tier_emojis: { ...f.tier_emojis, [key]: e.target.value } }))}
                        data-testid={`tier-emoji-${key}`}
                        placeholder="--"
                        className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg text-center text-lg focus:border-[#D44E20] focus:outline-none" />
                    </div>
                    {/* Name */}
                    <div className="col-span-3">
                      <input type="text" maxLength={20}
                        value={form.tier_names?.[key] ?? tierName}
                        onChange={e => setForm(f => ({ ...f, tier_names: { ...f.tier_names, [key]: e.target.value } }))}
                        data-testid={`tier-name-${key}`}
                        className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-sm text-[#1A1A1A] font-medium focus:border-[#D44E20] focus:outline-none" />
                    </div>
                    {/* Threshold */}
                    <div className="col-span-4">
                      {key === 'bronze' ? (
                        <span className="text-xs text-[#64748B]">Tier iniziale (0 pt)</span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[#64748B] text-xs flex-shrink-0">da</span>
                          <input type="number" min={0}
                            value={form.tier_thresholds?.[key] ?? ''}
                            onChange={e => setForm(f => ({ ...f, tier_thresholds: { ...f.tier_thresholds, [key]: e.target.value } }))}
                            data-testid={`tier-threshold-${key}`}
                            className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-sm text-[#1A1A1A] text-center font-semibold focus:border-[#D44E20] focus:outline-none" />
                          <span className="text-[#64748B] text-xs flex-shrink-0">pt</span>
                        </div>
                      )}
                    </div>
                    {/* Multiplier */}
                    <div className="col-span-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#64748B] text-xs flex-shrink-0">x</span>
                        <input type="number" min={0.1} step={0.05}
                          value={form.tier_multipliers?.[key] ?? ''}
                          onChange={e => setForm(f => ({ ...f, tier_multipliers: { ...f.tier_multipliers, [key]: e.target.value } }))}
                          data-testid={`tier-multiplier-${key}`}
                          className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-sm text-[#1A1A1A] text-center font-semibold focus:border-[#D44E20] focus:outline-none" />
                        <span className="text-[#64748B] text-xs flex-shrink-0">punti</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[#64748B] text-[10px] grid grid-cols-12 gap-3 px-4">
            <span className="col-span-1">Colore</span>
            <span className="col-span-1">Emoji</span>
            <span className="col-span-3">Nome</span>
            <span className="col-span-4">Soglia (punti lifetime)</span>
            <span className="col-span-3">Moltiplicatore</span>
          </div>
        </div>

        {/* Points expiry */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-1">Scadenza punti</h3>
          <p className="text-[#64748B] text-xs mb-4">I punti scaduti vengono rimossi automaticamente</p>

          <div className="flex items-center justify-between py-4 border-b border-gray-50">
            <div>
              <p className="text-[#1A1A1A] text-sm font-medium">Attiva scadenza</p>
              <p className="text-[#64748B] text-xs mt-0.5">I punti scadranno dopo il periodo configurato</p>
            </div>
            <button
              onClick={() => setForm(f => ({ ...f, points_expiry_enabled: !f.points_expiry_enabled }))}
              data-testid="expiry-toggle"
              className={`w-12 h-7 rounded-full transition-all relative ${form.points_expiry_enabled ? 'bg-[#D44E20]' : 'bg-gray-300'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm ${form.points_expiry_enabled ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {form.points_expiry_enabled && (
            <div className="animate-fade-in">
              {/* Mode selector */}
              <div className="py-4 border-b border-gray-50">
                <p className="text-[#1A1A1A] text-sm font-medium mb-2">Tipo di scadenza</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setForm(f => ({ ...f, points_expiry_mode: 'relative' }))}
                    data-testid="expiry-mode-relative"
                    className={`py-2.5 rounded-xl text-xs font-medium border transition-all ${
                      (form.points_expiry_mode || 'relative') === 'relative'
                        ? 'bg-[#D44E20] text-white border-[#D44E20]'
                        : 'bg-white text-[#64748B] border-slate-200'
                    }`}
                  >
                    Relativa
                    <span className="block text-[10px] opacity-80 font-normal">dopo X mesi</span>
                  </button>
                  <button
                    onClick={() => setForm(f => {
                      const v = f.points_expiry_fixed_date || '';
                      // YYYY-MM-DD (3 parts) is valid for fixed; otherwise reset.
                      const keep = /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '';
                      return { ...f, points_expiry_mode: 'fixed', points_expiry_fixed_date: keep };
                    })}
                    data-testid="expiry-mode-fixed"
                    className={`py-2.5 rounded-xl text-xs font-medium border transition-all ${
                      form.points_expiry_mode === 'fixed'
                        ? 'bg-[#D44E20] text-white border-[#D44E20]'
                        : 'bg-white text-[#64748B] border-slate-200'
                    }`}
                  >
                    Data fissa
                    <span className="block text-[10px] opacity-80 font-normal">es. 31/12/2026</span>
                  </button>
                  <button
                    onClick={() => setForm(f => {
                      const v = f.points_expiry_fixed_date || '';
                      // Keep MM-DD. If value is YYYY-MM-DD, keep the MM-DD tail.
                      let keep = '';
                      const mmdd = v.match(/^(\d{2})-(\d{2})$/);
                      const ymd = v.match(/^\d{4}-(\d{2})-(\d{2})$/);
                      if (mmdd) keep = v;
                      else if (ymd) keep = `${ymd[1]}-${ymd[2]}`;
                      return { ...f, points_expiry_mode: 'recurring', points_expiry_fixed_date: keep };
                    })}
                    data-testid="expiry-mode-recurring"
                    className={`py-2.5 rounded-xl text-xs font-medium border transition-all ${
                      form.points_expiry_mode === 'recurring'
                        ? 'bg-[#D44E20] text-white border-[#D44E20]'
                        : 'bg-white text-[#64748B] border-slate-200'
                    }`}
                  >
                    Ricorrente
                    <span className="block text-[10px] opacity-80 font-normal">ogni anno MM-DD</span>
                  </button>
                </div>
              </div>

              {(() => {
                const mode = form.points_expiry_mode || 'relative';
                if (mode === 'relative') {
                  return (
                    <Field
                      label="Mesi prima della scadenza"
                      desc="I punti guadagnati scadranno dopo questo periodo"
                      value={form.points_expiry_months}
                      onChange={v => setForm(f => ({ ...f, points_expiry_months: v }))}
                      min={1}
                    />
                  );
                }
                if (mode === 'fixed') {
                  return (
                    <FixedExpiryRow
                      value={form.points_expiry_fixed_date}
                      onChange={v => setForm(f => ({ ...f, points_expiry_fixed_date: v }))}
                    />
                  );
                }
                return (
                  <RecurringExpiryRow
                    value={form.points_expiry_fixed_date}
                    onChange={v => setForm(f => ({ ...f, points_expiry_fixed_date: v }))}
                  />
                );
              })()}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-800 text-sm font-medium">Elabora scadenze</p>
                  <p className="text-amber-700 text-xs mt-0.5 mb-2">Rimuove i punti scaduti dai saldi dei clienti. Questa azione non è reversibile.</p>
                  <button
                    onClick={async () => {
                      setExpiring(true);
                      try {
                        const res = await adminApi.post('/admin/expire-points');
                        toast.success(res.data.message);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Errore');
                      } finally { setExpiring(false); }
                    }}
                    disabled={expiring}
                    data-testid="process-expiry-btn"
                    className="px-4 py-2 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                  >
                    {expiring ? 'Elaborazione...' : 'Elabora ora'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Absolute YYYY-MM-DD date picker for the "Data fissa" mode. Single
// calendar input; values roundtrip as "YYYY-MM-DD". Past dates are
// tolerated (the backend treats them as already-expired anniversaries
// via `_resolve_recurring_expiry_date`).
function FixedExpiryRow({ value, onChange }) {
  const safe = /^\d{4}-\d{2}-\d{2}$/.test(value || '') ? value : '';
  const previewText = (() => {
    if (!safe) return null;
    try {
      const [y, m, d] = safe.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      return dt.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return null; }
  })();
  return (
    <div className="py-4 border-b border-gray-50">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[#1A1A1A] text-sm font-medium">Data di scadenza fissa</p>
          <p className="text-[#64748B] text-xs mt-0.5">Tutti i punti scadranno esattamente in questa data (una tantum).</p>
        </div>
        <input
          type="date"
          value={safe}
          onChange={e => onChange(e.target.value || '')}
          data-testid="expiry-fixed-date"
          className="h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none"
        />
      </div>
      {previewText && (
        <p className="text-[11px] text-[#64748B] mt-2 ml-1" data-testid="expiry-fixed-preview">
          Scadenza: <span className="font-semibold text-[#1A1A1A]">{previewText}</span>
        </p>
      )}
    </div>
  );
}

// MM-DD recurring date picker. Stores in form as "MM-DD" string.
const MONTHS_IT = [
  'Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
  'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre',
];
function _parseExpiryValue(raw) {
  if (!raw || typeof raw !== 'string') return { month: '', day: '' };
  const parts = raw.split('-');
  if (parts.length === 2) return { month: parts[0], day: parts[1] };
  if (parts.length === 3) return { month: parts[1], day: parts[2] };
  return { month: '', day: '' };
}
function RecurringExpiryRow({ value, onChange }) {
  const { month, day } = _parseExpiryValue(value);
  const monthInt = parseInt(month, 10) || 0;
  const daysInMonth = monthInt
    ? new Date(2024, monthInt, 0).getDate() // 2024 is leap → max 29 in Feb
    : 31;
  const update = (m, d) => {
    if (!m && !d) { onChange(''); return; }
    if (!m || !d) { onChange(`${m || ''}-${d || ''}`); return; }
    const mm = String(parseInt(m, 10)).padStart(2, '0');
    const dd = String(Math.min(parseInt(d, 10), daysInMonth)).padStart(2, '0');
    onChange(`${mm}-${dd}`);
  };
  const previewText = (() => {
    if (!month || !day) return null;
    const d = parseInt(day, 10), m = parseInt(month, 10);
    if (!d || !m) return null;
    const now = new Date();
    let next = new Date(now.getFullYear(), m - 1, d);
    if (next < now) next = new Date(now.getFullYear() + 1, m - 1, d);
    return next.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  })();
  return (
    <div className="py-4 border-b border-gray-50">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[#1A1A1A] text-sm font-medium">Data di scadenza ricorrente</p>
          <p className="text-[#64748B] text-xs mt-0.5">I punti scadono ogni anno in questa data (es. 28 febbraio).</p>
        </div>
        <div className="flex gap-2">
          <select
            value={day}
            onChange={e => update(month, e.target.value)}
            data-testid="expiry-day-select"
            className="h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none"
          >
            <option value="">Giorno</option>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
              <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
            ))}
          </select>
          <select
            value={month}
            onChange={e => update(e.target.value, day)}
            data-testid="expiry-month-select"
            className="h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none"
          >
            <option value="">Mese</option>
            {MONTHS_IT.map((name, i) => (
              <option key={i} value={String(i + 1).padStart(2, '0')}>{name}</option>
            ))}
          </select>
        </div>
      </div>
      {previewText && (
        <p className="text-[11px] text-[#64748B] mt-2 ml-1" data-testid="expiry-preview">
          Prossima scadenza: <span className="font-semibold text-[#1A1A1A]">{previewText}</span> · poi ogni anno alla stessa data
        </p>
      )}
    </div>
  );
}
